import { useMemoizedFn, useUpdateEffect } from "ahooks";
import { useEffect, useRef, useState } from "react";

export function useInfiniteList<T>(
  queryFn: (page?: number) => Promise<T[] | { list: T[] }>,
  options?: {
    initialData?: T[];
    updateEffectDeps?: any[];
    hasMoreFn?: (list: T[]) => boolean;
  }
) {
  const [list, setList] = useState<T[]>(options?.initialData || []);
  const [hasMore, setHasMore] = useState(true);
  const pageRef = useRef(options?.initialData?.length ? 2 : 1);

  const loadData = useCanceledFn(queryFn);
  const [isLoading, setIsLoading] = useState(false);

  const onLoadMore = useMemoizedFn(async () => {
    setIsLoading(true);
    const res = await loadData(pageRef.current);

    // 使用类型守卫判断是否是取消状态
    if ("canceled" in res) {
      setIsLoading(false);
      return;
    }

    const list = res instanceof Array ? res : res.list;

    if (list.length > 0) {
      setList((prev) => prev.concat(list));
      pageRef.current += 1;
      setHasMore(options?.hasMoreFn ? options.hasMoreFn(list) : true);
    } else {
      setHasMore(false);
    }
    setIsLoading(false);
  });

  const resetData = useMemoizedFn(() => {
    setList([]);
    pageRef.current = 1;
    setHasMore(true);
    onLoadMore();
  });

  const hasInitData = useRef(options?.initialData?.length > 0);
  useEffect(() => {
    if (!hasInitData.current) {
      // 没有初始数据时，自动加载第一页数据
      onLoadMore();
    }
  }, [onLoadMore]);

  useUpdateEffect(() => {
    resetData();
  }, options?.updateEffectDeps || []);

  return {
    list,
    setList,
    hasMore,
    page: pageRef.current,
    onLoadMore,
    resetData,
    loading: isLoading,
  };
}

export function useCanceledFn<T extends (...args: any[]) => Promise<any>>(
  fn: T
) {
  const countRef = useRef(0);

  return useMemoizedFn(
    async (
      ...args: Parameters<T>
    ): Promise<ReturnType<T> | { canceled: true }> => {
      countRef.current += 1;
      const currentCount = countRef.current;
      const result = await fn(...args);

      // prevent run.then when request is canceled
      if (currentCount !== countRef.current) {
        return {
          canceled: true,
        };
      }

      return result;
    }
  );
}
